import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import {
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Container,
  IconButton,
  Popover,
  Snackbar,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import { useGameUtils } from './utilities/GameUtils';
import { useError } from './contexts/ErrorContext';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import onCourseIcon from './assets/images/icon-game-oncourse@2x.png';
import offCourseIcon from './assets/images/icon-game-offcourse@2x.png';
import otherIcon from './assets/images/icon-game-practice@2x.png';
import gamePlayIcon from './assets/images/icon-gameFormat-play@2x.png';
import practiceIcon from './assets/images/icon-gameFormat-practice@2x.png';
import iconHidden from './assets/images/icon-hidden.svg';
import iconClose from './assets/images/icon-close-x.svg';
import calendarPickerIcon from './assets/images/icon-calendar-picker.svg';
import InfoIcon from '@mui/icons-material/Info';
import { useApi } from './services/api';
dayjs.locale('en-gb');

const createSearchableString = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const GameForm = () => {
  const navigate = useNavigate();
  const { id = null } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    date: null,
    clubId: '',
    clubName: '',
    courseId: '',
    courseName: '',
    coursePar: 0,
    format: '',
    maxPlayers: 4,
    createdBy: '',
    addCosts: false,
    cost: 0,
    costDescription: '',
    splitCosts: false,
    visibility: 'Private',
    gameType: 'On Course',
    active: true,
    dateTimeOptions: [],
    shareCode: Math.random().toString(36).substring(2, 8),
    status: 'pending',
    competitive: true,
    overallScore: null,
    whatsAppGroupInviteLink: '',
  });

  const [location, setLocation] = useState('');
  const [clubSuggestions, setClubSuggestions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const { db } = useFirebase();
  const { currentUser } = useAuth();
  const { fetchGameById } = useGameUtils();
  const { logFirebaseError } = useError();
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarDate, setCalendarDate] = useState(dayjs(new Date()));
  const [clockTime, setClockTime] = useState(dayjs(new Date()).format('HH:mm'));
  const [infoMessage, setInfoMessage] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const openPicker = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePicker = () => {
    setAnchorEl(null);
  };

  const handleCalendarChange = (newDate) => {
    if (!newDate || typeof newDate !== 'object' || typeof newDate.isValid !== 'function') {
      console.warn('Invalid date object from DateCalendar:', newDate);
      return;
    }

    // Use toISOString to safely verify it's usable
    try {
      console.log('Calendar selected:', newDate.toISOString());
      setCalendarDate(newDate); // safe to store as-is
    } catch (err) {
      console.warn('Invalid dayjs object passed from DateCalendar:', newDate);
    }
  };

  const handleClockChange = (event) => {
    setClockTime(event.target.value);
  };

  const handleDone = () => {
    if (
      !calendarDate ||
      !clockTime ||
      !dayjs(calendarDate).isValid() ||
      !clockTime.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    ) {
      console.warn('Invalid date/time selected');
      closePicker();
      return;
    }

    const [hours, minutes] = clockTime.split(':').map(Number);
    const combined = dayjs(calendarDate).hour(hours).minute(minutes).second(0).millisecond(0);

    if (combined.isValid()) {
      handleDateChange(combined);
    } else {
      console.warn('Failed to combine date/time');
    }

    closePicker();
  };

  const isOrganiser = () => {
    return currentUser && currentUser.uid === formData.createdBy;
  };

  const fetchClubSuggestions = useCallback(
    async (searchTerm) => {
      try {
        const clubsRef = collection(db, 'clubs');
        const searchableSearchTerm = createSearchableString(searchTerm);
        const clubsQuery = query(clubsRef, where('searchableClubName', '>=', searchableSearchTerm));
        const clubSnapshot = await getDocs(clubsQuery);
        const clubList = clubSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setClubSuggestions(clubList);
      } catch (error) {
        console.error('Error fetching club suggestions:', error);
        await logFirebaseError(error, 'fetch_club_suggestions');
      }
    },
    [db, logFirebaseError]
  );

  const loadGame = useCallback(
    async (gameId) => {
      try {
        const gameData = await fetchGameById(gameId);
        setFormData((prevData) => ({
          ...gameData,
          shareCode: gameData.shareCode || prevData.shareCode,
        }));
        setLocation(gameData.clubName);
      } catch (error) {
        console.error('Error loading game:', error);
        await logFirebaseError(error, 'load_game', { gameId });
      }
    },
    [fetchGameById, logFirebaseError]
  );

  const fetchCourseOptions = useCallback(
    async (clubId) => {
      try {
        const coursesRef = collection(db, 'courses');
        const coursesQuery = query(coursesRef, where('clubId', '==', clubId));
        const coursesSnapshot = await getDocs(coursesQuery);
        const coursesList = coursesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCourseOptions(coursesList);
        if (coursesList.length > 0) {
          setFormData((prevData) => ({
            ...prevData,
            courseId: coursesList[0].courseId,
            courseName: coursesList[0].courseName,
            coursePar: coursesList[0].par,
          }));
        }
      } catch (error) {
        console.error('Error fetching course options:', error);
        await logFirebaseError(error, 'fetch_course_options');
      }
    },
    [db, logFirebaseError]
  );

  useEffect(() => {
    if (id) {
      loadGame(id);
    }
  }, [id, loadGame]);

  useEffect(() => {
    if (formData.clubId) {
      fetchCourseOptions(formData.clubId);
    }
  }, [formData.clubId, fetchCourseOptions]);

  useEffect(() => {
    if (location.length === 1) {
      fetchClubSuggestions(location);
    }
  }, [location, fetchClubSuggestions]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
      // date: prevData.date ? new Date(prevData.date) : null
    }));
  };

  const handleDateChange = (dt) => {
    // if (formData.prospectiveDates.length > 2) return;

    setFormData(function (prevData) {
      prevData.dateTimeOptions = prevData.dateTimeOptions || [];
      prevData.dateTimeOptions.push({
        option: Timestamp.fromDate(new Date(dt)),
        acceptedBy: [],
        archived: false,
        id: prevData.dateTimeOptions.length + 1,
      });
      return {
        ...prevData,
      };
    });
  };

  const handleDateDelete = (timestampToDelete) => {
    setFormData((prevData) => ({
      ...prevData,
      dateTimeOptions: prevData.dateTimeOptions.filter((ts) => ts !== timestampToDelete),
    }));
  };

  const handleLocationChange = (val) => {
    setFormData((prevData) => ({
      ...prevData,
      clubId: val.clubId,
      clubName: val.clubName,
      clubAddress: val.address,
      clubPostCode: val.postalCode,
      clubLatitude: val.latitude,
      clubLongitude: val.longitude,
    }));
  };

  const handleGameTypeChange = (event, newGameType) => {
    if (newGameType === 'On Course') {
      setFormData((prevData) => ({
        ...prevData,
        gameType: newGameType,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        gameType: newGameType,
        clubAddress: '',
        clubPostCode: '',
        clubId: '',
        clubName: '',
        clubLatitude: '',
        clubLongitude: '',
      }));
    }
  };

  const handleCourseChange = (event) => {
    const courseId = event.target.value;
    const selectedCourse = courseOptions.find((course) => course.courseId === courseId);

    setFormData((prevData) => ({
      ...prevData,
      courseId: courseId,
      courseName: selectedCourse?.courseName || '',
      coursePar: selectedCourse?.par || '',
    }));
  };

  // const handleSplitPaymentChange = (event) => {
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     splitCosts: event.target.checked,
  //   }));
  // };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();

    try {
      if (id) {
        console.log('a');
        const gameRef = doc(db, 'games', id);
        await updateDoc(gameRef, {
          ...formData,
          cost: +formData.cost,
          updatedBy: currentUser.uid,
          updatedOn: new Date(),
        });

        navigate(`/games/${id}`);
      } else {
        const _formDataDateTimeOptions = formData.dateTimeOptions.map((option) => {
          const _acceptedBy = [
            {
              uid: currentUser.uid,
              userName: currentUser.displayName,
              acceptedAt: Timestamp.fromDate(new Date()),
            },
          ];
          return {
            ...option,
            acceptedBy: _acceptedBy,
          };
        });
        console.log('_formDataDateTimeOptions', _formDataDateTimeOptions);
        let dataToSave = {
          ...formData,
          cost: +formData.cost,
          createdBy: currentUser.uid,
          shareCode: formData.shareCode,
          dateTimeOptions: _formDataDateTimeOptions,
          createdAt: Timestamp.fromDate(new Date()),
          declinedBy: [],
        };
        Object.keys(dataToSave).forEach((key) => {
          if (dataToSave[key] === undefined) {
            delete dataToSave[key];
          }
        });

        const skipPending = _formDataDateTimeOptions?.length === 1;

        if (skipPending) {
          dataToSave.status = 'confirmed';
          dataToSave.date = _formDataDateTimeOptions?.[0].option;
          dataToSave.dateId = _formDataDateTimeOptions?.[0].id;
        }

        const gameRef = await addDoc(collection(db, 'games'), dataToSave);
        const snapshot = await getDoc(gameRef);
        if (snapshot) {
          const gameId = snapshot.id;
          if (skipPending) {
            await post('/game/game-confirmed', {
              gameId: gameId,
            });
          }
          navigate(`/games/${gameId}/invite`, {
            state: {
              isNewGame: true,
            },
          });
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding document: ', error);
      await logFirebaseError(error, id ? 'update_game' : 'create_game', {
        gameId: id,
        hasFormData: !!formData,
        operation: id ? 'update' : 'create',
      });
      alert('Failed to save game.');
    }
    setLoading(false);
  };

  const saveEnabled = () => {
    if (id) {
      return isOrganiser();
    }

    let enabled = true;
    if (formData.date) {
      const now = new Date();
      const gameDate = formData.date.toDate ? formData.date.toDate() : new Date(formData.date);
      if (gameDate < now) {
        enabled = false;
      } else {
        const timeDifference = gameDate - now;
        const hoursDifference = timeDifference / (1000 * 60 * 60);
        if (hoursDifference <= 48) {
          enabled = false;
        }
      }
    }
    return enabled;
  };

  const handleInfoClick = (message) => (event) => {
    event.stopPropagation(); // Prevent the toggle button from being triggered
    setInfoMessage(message);
    setShowInfo(true);
  };

  const handleInfoClose = () => {
    setShowInfo(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Container maxWidth="sm" sx={{ paddingTop: 2 }}>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          <Typography variant="h2" sx={{ textAlign: 'center', paddingBottom: 2 }}>
            {id ? `Edit Game` : `Start a Game`}
          </Typography>
          <TextField
            variant="outlined"
            name="name"
            placeholder="Give this game a name"
            value={formData.name}
            onChange={(e) => handleFormChange(e)}
            fullWidth
            required
          />

          <Typography variant="formHeader">When</Typography>
          <Typography variant="formText" component="small">
            Add one or more day/time options. Participants will vote on dates they wish to
            participate. Works for single rounds, range sessions, or multiple tee times on the same
            day.
          </Typography>

          <Box
            sx={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: 1,
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'flex-start',
              minHeight: 56,
              gap: 1,
            }}
            onClick={(e) => {
              openPicker(e);
            }}
          >
            {/* Left column: the icon */}
            <Stack sx={{ flexShrink: 0, mt: '4px', mb: '8px', alignItems: 'center' }}>
              <IconButton size="small" onClick={openPicker} sx={{ paddingBottom: 0 }}>
                <img src={calendarPickerIcon} alt="calendar picker" />
              </IconButton>
              <Typography component="small" sx={{ fontWeight: '600', fontSize: '14px' }}>
                +dates
              </Typography>
            </Stack>

            {/* Right column: the chips, wrapping as needed */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                flexGrow: 1,
                pt: '4px',
              }}
            >
              {(formData.dateTimeOptions || []).map((d, index) => {
                if (d.archived) return null;
                return (
                  <Chip
                    key={index}
                    label={d.option.toDate().toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    color="default"
                    onDelete={() => handleDateDelete(d)}
                    variant="gameDate"
                    deleteIcon={<img src={iconClose} alt="x" />}
                  />
                );
              })}
            </Box>
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closePicker}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <Box p={2} display="flex" flexDirection="column" alignItems="center">
                <DateCalendar value={calendarDate} onChange={handleCalendarChange} disablePast />
                <Box mt={2} />
                <TextField
                  type="time"
                  value={clockTime}
                  onChange={handleClockChange}
                  inputProps={{
                    min: dayjs(calendarDate).isSame(dayjs(), 'day')
                      ? dayjs().format('HH:mm')
                      : undefined,
                  }}
                  sx={{ width: '100%' }}
                />
                <Box mt={2}>
                  <Button variant="contained" onClick={handleDone}>
                    Done
                  </Button>
                </Box>
              </Box>
            </LocalizationProvider>
          </Popover>

          <Typography variant="formHeader">Where</Typography>

          <ToggleButtonGroup
            exclusive
            value={formData.gameType}
            name="gameType"
            onChange={handleGameTypeChange}
            fullWidth={true}
          >
            <ToggleButton
              name="gameType"
              value="On Course"
              aria-label="On Course"
              sx={{ flexDirection: 'column' }}
            >
              <img
                src={onCourseIcon}
                alt="on course icon"
                width="48"
                style={{ pointerEvents: 'none' }}
              />
              Golf Course
            </ToggleButton>
            <ToggleButton
              name="gameType"
              value="Off Course"
              aria-label="Off Course"
              sx={{ flexDirection: 'column' }}
            >
              <img
                src={offCourseIcon}
                alt="off course icon"
                width="48"
                style={{ pointerEvents: 'none' }}
              />
              Simulator / Driving Range
            </ToggleButton>
            <ToggleButton
              name="gameType"
              value="Practice"
              aria-label="Practice"
              sx={{ flexDirection: 'column' }}
            >
              <img
                src={otherIcon}
                alt="practice icon"
                width="48"
                style={{ pointerEvents: 'none' }}
              />
              Other
            </ToggleButton>
          </ToggleButtonGroup>

          {formData.gameType === 'On Course' && (
            <>
              <Autocomplete
                freeSolo
                disableClearable
                inputValue={location}
                options={clubSuggestions}
                placeholder="Search for a club"
                getOptionLabel={(option) => option.clubName}
                filterOptions={(options, { inputValue }) => {
                  const normalizedInput = createSearchableString(inputValue);
                  return options.filter((option) =>
                    createSearchableString(option.searchableClubName).includes(normalizedInput)
                  );
                }}
                onInputChange={(event, newInputValue) => setLocation(newInputValue)}
                onChange={(event, newValue) => handleLocationChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Search for a club"
                    slotProps={{ inputLabel: { shrink: false } }}
                  />
                )}
              />

              <FormControl fullWidth>
                {/* <InputLabel>Select Course</InputLabel> */}
                <Select
                  name="courseId"
                  placeholder="Select a Course"
                  value={formData.courseId}
                  onChange={handleCourseChange}
                  disabled={courseOptions.length === 0}
                >
                  {courseOptions.map((course) => (
                    <MenuItem key={course.id} value={course.courseId}>
                      {course.courseName} (Par {course.par})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {formData.gameType !== 'On Course' && (
            <>
              <TextField
                label="Address"
                multiline
                maxRows={4}
                value={formData.clubAddress}
                onChange={(e) => handleFormChange(e)}
                name="clubAddress"
              />
              <TextField
                label="Post Code"
                value={formData.clubPostCode}
                onChange={(e) => handleFormChange(e)}
                name="clubPostCode"
              />
            </>
          )}

          <Typography variant="formHeader">Type</Typography>

          <ToggleButtonGroup
            exclusive
            value={String(formData.competitive)}
            name="competitive"
            onChange={(event, newValue) => {
              if (newValue !== null) {
                handleFormChange({
                  target: {
                    name: 'competitive',
                    value: newValue === 'true',
                  },
                });
              }
            }}
            fullWidth={true}
          >
            <ToggleButton
              name="competitive"
              value="true"
              aria-label="Competitive"
              sx={{ flexDirection: 'column', position: 'relative' }}
            >
              <IconButton
                size="small"
                onClick={handleInfoClick(
                  'Competitive games affect your WAGL rating and contribute to your overall performance metrics.'
                )}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: '4px',
                }}
              >
                <InfoIcon fontSize="small" color="primary" />
              </IconButton>
              <img
                src={gamePlayIcon}
                alt="game play icon"
                width="48"
                style={{ pointerEvents: 'none' }}
              />
              Competitive
            </ToggleButton>
            <ToggleButton
              name="competitive"
              value="false"
              aria-label="Friendly"
              sx={{ flexDirection: 'column', position: 'relative' }}
            >
              <IconButton
                size="small"
                onClick={handleInfoClick(
                  "Friendly games are casual matches that don't affect your WAGL rating - perfect for practice and social play."
                )}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: '4px',
                }}
              >
                <InfoIcon fontSize="small" color="primary" />
              </IconButton>
              <img
                src={practiceIcon}
                alt="range icon"
                width="48"
                style={{ pointerEvents: 'none' }}
              />
              Friendly
            </ToggleButton>
          </ToggleButtonGroup>

          <Snackbar
            open={showInfo}
            autoHideDuration={4000}
            onClose={handleInfoClose}
            message={infoMessage}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />

          {/* <Typography variant="formHeader">Format</Typography>
          <ToggleButtonGroup
            exclusive
            value={formData.format}
            name="kind"
            onChange={(e) => handleFormChange(e)}
            fullWidth={true}
          >
            <ToggleButton
              name="format"
              value="Stroke Play"
              aria-label="Stroke Play"
              sx={{ flexDirection: 'column' }}
            >
              Stroke Play
            </ToggleButton>
            <ToggleButton
              name="format"
              value="Match Play"
              aria-label="Match Play"
              sx={{ flexDirection: 'column' }}
            >
              Match Play
            </ToggleButton>
            <ToggleButton
              name="format"
              value="Scramble"
              aria-label="Scramble"
              sx={{ flexDirection: 'column' }}
            >
              Scramble
            </ToggleButton>
          </ToggleButtonGroup> */}

          {/* <FormControl fullWidth sx={{ marginY: '24px' }}>
            <Typography variant="fieldLabel">Max Players</Typography>
            <Slider
              name="maxPlayers"
              min={0}
              max={4}
              value={formData.maxPlayers}
              onChange={(e, val) => handleFormChange(e)}
              marks
              step={1}
              valueLabelDisplay="on"
              valueLabelFormat={formatValueLabel}
            />
          </FormControl> */}

          <FormControl sx={{ mb: 2 }}>
            <Typography variant="fieldLabel" sx={{ display: 'flex', gap: '8px' }} component="p">
              Game Visibility <img src={iconHidden} alt="visibility icon" />
            </Typography>
            <RadioGroup
              row
              name="visibility"
              value={formData.visibility}
              onChange={handleFormChange}
            >
              <FormControlLabel
                value="Private"
                control={<Radio />}
                label={
                  <span>
                    <strong>Hidden</strong> from feeds, invite-only via link
                  </span>
                }
              />
              <FormControlLabel
                value="Open"
                control={<Radio />}
                label={
                  <span>
                    <strong>Appears</strong> in feeds
                  </span>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* <FormControl sx={{ mb: 0 }}>
            <Typography variant="fieldLabel" sx={{ display: 'flex', gap: '8px' }} component="p">
              WAGL Score
            </Typography>
            <RadioGroup
              row
              name="competitive"
              value={String(formData.competitive)}
              onChange={(e) => {
                const value = e.target.value === 'true';
                handleFormChange({
                  target: {
                    name: 'competitive',
                    value: value,
                  },
                });
              }}
            >
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={
                  <span>
                    <strong>Competitive</strong> results will affect Wagl ratings
                  </span>
                }
              />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={
                  <span>
                    <strong>Friendly</strong>
                  </span>
                }
              />
            </RadioGroup>
          </FormControl> */}

          <Typography variant="fieldLabel">Whats App Group Invite Link</Typography>
          <TextField
            name="whatsAppGroupInviteLink"
            value={formData.whatsAppGroupInviteLink}
            onChange={(e) => handleFormChange(e)}
            placeholder="e.g. https://chat.whatsapp.com/..."
          />

          <Typography variant="fieldLabel">Extra Details</Typography>
          <TextField
            name="extraDetails"
            value={formData.extraDetails}
            // label="Multiline Placeholder"
            placeholder="Anything else you need to let the crew know? Parking? Pub? Dress Code?"
            multiline
            onChange={(e) => handleFormChange(e)}
            rows={4}
            variant="outlined"
          />

          {/* <Typography variant="h5">Green Fees (Optional)</Typography> */}

          {/* <Stack direction="row" spacing={2}> */}
            {/* <TextField
              label="i.e. green fees, lunch, etc"
              name="costDescription"
              value={formData.costDescription}
              onChange={handleFormChange}
              sx={{ flex: '1 1 70%' }}
            /> */}
            <Typography variant="fieldLabel">Green Fees (per person)</Typography>
            <TextField
              name="cost"
              type="number"
              label="Total"
              value={formData.cost}
              onChange={handleFormChange}
              sx={{ flex: '1 1 30%' }}
            />
          {/* </Stack> */}

          {/* <Box sx={{ marginY: '16px' }}>
            <Typography variant="fieldLabel">Split Costs</Typography>
            <Stack direction="row" spacing={4}>
              <Typography>Do you want to split the cost evenly with other players?</Typography>
              <Switch
                checked={formData.splitCosts}
                name="splitCosts"
                onChange={(e) => handleSplitPaymentChange(e)}
              />
            </Stack>
          </Box> */}

          <Button
            type="submit"
            disabled={loading || !saveEnabled()}
            variant="containedWagl"
            color="primary"
          >
            Continue
          </Button>
        </form>
      </Container>
    </Box>
  );
};

export default GameForm;
